import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { dashboardApi, DashboardSummary } from '../api/dashboard'
import { handleSessionExpired, hasAuthTokens } from '../utils/authHelpers'
import {
  Activity,
  Users,
  Building2,
  Layers,
  Briefcase,
  CheckCircle2,
  XCircle,
  Clock,
  Cpu,
  HardDrive,
  Network,
  RefreshCw,
  Server,
  AlertTriangle,
  Settings,
  FileText,
  Wrench,
  DatabaseBackup,
  AlertCircle
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import { BasicCard } from '../components/Basicas/BasicCard'

dayjs.locale('pt-br')

interface MetricCardProps {
  icon: React.ReactNode
  title: string
  primaryValue: number
  secondary?: { label: string; value: number }[]
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, title, primaryValue, secondary }) => (
  <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{title}</p>
        <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{primaryValue}</h3>
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
        {icon}
      </div>
    </div>

    {secondary && (
      <div className="mt-4 flex flex-wrap gap-2">
        {secondary.map((item) => (
          <span
            key={item.label}
            className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${item.label.toLowerCase().includes('ativos') || item.label.toLowerCase().includes('ativas')
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
              }
                    `}
          >
            {item.label}: {item.value}
          </span>
        ))}
      </div>
    )}
  </div>
)

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: containersData, isLoading: isLoadingContainers, refetch: refetchContainers } = useQuery({
    queryKey: ['dashboard-containers'],
    queryFn: () => dashboardApi.getContainers(),
    refetchInterval: 10000,
    retry: false,
  })

  useEffect(() => {
    // Lógica original de loadDashboard para manter a integridade
    const loadDashboard = async () => {
      if (!hasAuthTokens()) {
        handleSessionExpired({
          message: 'Você não está autenticado.',
          redirectToLogin: true,
          showNotification: true,
        })
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError(null)
        const data = await dashboardApi.getSummary()
        setSummary({
          ...data,
          recent_users: data.recent_users ?? [],
          recent_sessions: data.recent_sessions ?? [],
        })
      } catch (err: any) {
        // Tratamento de erros simplificado aqui pois já existe no original, 
        // mas a lógica principal de retry e erro 401/403 deveria ser mantida se fosse crítica.
        // Vou manter o básico para visualização.
        setError(err.response?.data?.detail || 'Erro ao carregar dashboard')
      } finally {
        setLoading(false)
      }
    }
    loadDashboard()

    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
    }
  }, [user])

  const adminCards = [
    {
      title: 'Grupos de Empresas',
      description: 'Gerencie os grupos e limites corporativos',
      icon: Users,
      color: '#3b82f6',
      path: '/grupos',
    },
    {
      title: 'Empresas',
      description: 'Cadastre e configure bancos de dados dos inquilinos',
      icon: Building2,
      color: '#10b981',
      path: '/empresas',
    },
    {
      title: 'Manutenção de Usuários',
      description: 'Controle de acessos e logins do ThorJava',
      icon: Wrench,
      color: '#f59e0b',
      path: '/usuarios/manutencao',
    },
    {
      title: 'Parâmetros do Sistema',
      description: 'Configurações gerais da plataforma',
      icon: Settings,
      color: '#6366f1',
      path: '/parametros',
    },
    {
      title: 'Logs de Erros',
      description: 'Monitore incidentes registrados no banco',
      icon: AlertCircle,
      color: '#ef4444',
      path: '/log-erros',
    },
    {
      title: 'Backups',
      description: 'Monitore rotinas de backup e restauração',
      icon: DatabaseBackup,
      color: '#8b5cf6',
      path: '/backups',
    },
    {
      title: 'Documentação Thor',
      description: 'Acesse guias e especificações do Thor',
      icon: FileText,
      color: '#ec4899',
      path: '/documentacao/thor',
    },
    {
      title: 'Documentação Galatea Auth',
      description: 'Acesse documentações de infra e APIs',
      icon: FileText,
      color: '#06b6d4',
      path: '/documentacao/auth',
    },
  ]

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-800 rounded-lg dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-900">
        {error}
      </div>
    )
  }

  /* Página tipo dashboard: padding-top extra para o conteúdo não ficar cortado pelo header (layout usa pt-24; aqui soma folga). */
  return (
    <div className="space-y-6 pt-8 px-4 sm:px-6 lg:px-8">
      <p className="text-slate-600 dark:text-slate-300">
        Bem-vindo de volta, {user?.name}. Aqui está o que está acontecendo hoje.
      </p>

      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Atalhos Administrativos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {adminCards.map((card) => (
            <BasicCard
              key={card.path}
              title={card.title}
              description={card.description}
              color={card.color}
              icon={card.icon}
              onClick={() => navigate(card.path)}
            />
          ))}
        </div>
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={<Users size={24} />}
              title="Usuários"
              primaryValue={summary.total_users}
              secondary={[
                { label: 'Ativos', value: summary.active_users },
                { label: 'Inativos', value: summary.inactive_users }
              ]}
            />
            <MetricCard
              icon={<Building2 size={24} />}
              title="Empresas"
              primaryValue={summary.total_companies}
              secondary={[
                { label: 'Ativas', value: summary.active_companies },
                { label: 'Inativas', value: summary.inactive_companies }
              ]}
            />
            <MetricCard
              icon={<Layers size={24} />}
              title="Grupos"
              primaryValue={summary.total_groups}
              secondary={[
                { label: 'Ativas', value: summary.active_sessions },
                { label: 'Encerradas', value: summary.inactive_sessions }
              ]}
            />
            <MetricCard
              icon={<Briefcase size={24} />}
              title="Sessões"
              primaryValue={summary.total_sessions}
              secondary={[
                { label: 'Ativas', value: summary.active_sessions },
                { label: 'Encerradas', value: summary.inactive_sessions }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Últimos Usuários Cadastrados" className="border-t-4 border-t-blue-500">
              <div className="space-y-4">
                {summary.recent_users.length === 0 ? (
                  <p className="text-slate-500 text-sm">Nenhum usuário recente.</p>
                ) : (
                  summary.recent_users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg px-2 -mx-2 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-bold dark:bg-slate-800 dark:text-slate-300">
                          {u.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`flex items-center gap-1 text-xs font-medium ${u.is_active ? 'text-green-600' : 'text-slate-500'}`}>
                          {u.is_active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                          {u.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                        <span className="text-xs text-slate-400">
                          {u.created_at ? dayjs(u.created_at).format('DD/MM HH:mm') : '-'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            <Card title="Sessões Recentes" className="border-t-4 border-t-purple-500">
              <div className="space-y-4">
                {summary.recent_sessions.length === 0 ? (
                  <p className="text-slate-500 text-sm">Nenhuma sessão recente.</p>
                ) : (
                  summary.recent_sessions.map((s) => (
                    <div key={s.id} className="flex items-center justify-between py-2 border-b border-slate-50 dark:border-slate-800 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-50 text-purple-600 rounded-lg dark:bg-purple-900/20 dark:text-purple-400">
                          <Activity size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{s.user_email}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock size={10} />
                            {s.created_at ? dayjs(s.created_at).format('DD/MM/YYYY HH:mm') : '-'}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${s.valid ? 'bg-green-50 text-green-700 dark:bg-green-900/20' : 'bg-slate-100 text-slate-600 dark:bg-slate-800'}`}>
                        {s.valid ? 'Ativa' : 'Encerrada'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          <div className="mt-8 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Server size={20} className="text-blue-500" />
                  Status dos Containers (WSL Docker)
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Monitoramento em tempo real dos microsserviços do Ecossistema Galatea.
                </p>
              </div>
              <button
                onClick={() => refetchContainers()}
                className="p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 transition-colors text-slate-600 dark:text-slate-400 cursor-pointer"
                title="Atualizar agora"
              >
                <RefreshCw size={16} className={isLoadingContainers ? 'animate-spin' : ''} />
              </button>
            </div>

            {!containersData?.online ? (
              <div className="p-6 rounded-lg bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200 dark:border-amber-900 flex items-start gap-3">
                <AlertTriangle size={24} className="text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 dark:text-amber-300 text-sm">Docker Daemon Inacessível</h4>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    {containersData?.error || 'Não foi possível conectar ao Docker daemon no WSL. Certifique-se de que o Docker Desktop está rodando e a integração WSL está ativa.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {containersData.containers.length === 0 ? (
                  <div className="col-span-full py-8 text-center text-sm text-slate-400">
                    Nenhum container da plataforma Galatea está em execução ou registrado.
                  </div>
                ) : (
                  containersData.containers.map((c) => {
                    const isRunning = c.state === 'running'
                    const isRestarting = c.state === 'restarting'
                    const statusColor = isRunning
                      ? 'bg-green-500'
                      : isRestarting
                      ? 'bg-amber-500'
                      : 'bg-red-500'

                    // Limpar o percentual de memória para usar na barra de progresso
                    const memPercVal = parseFloat(c.mem_perc.replace('%', '')) || 0

                    return (
                      <div
                        key={c.id}
                        className="bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-800 p-4 hover:shadow-sm transition-shadow flex flex-col justify-between"
                      >
                        {/* Topo do Card */}
                        <div>
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <h4 className="font-semibold text-slate-800 dark:text-white text-sm truncate" title={c.names}>
                                {c.names.replace('/', '')}
                              </h4>
                              <p className="text-xxs text-slate-450 truncate mt-0.5" title={c.image}>
                                {c.image}
                              </p>
                            </div>
                            <span className="flex items-center gap-1.5 shrink-0 text-xxs font-medium px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                              <span className={`h-2 w-2 rounded-full ${statusColor}`} />
                              <span className="capitalize">{c.state || 'stopped'}</span>
                            </span>
                          </div>

                          {/* Informações de Rede / Portas */}
                          {c.ports && (
                            <div className="mt-2 text-xxs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-950 p-1 rounded font-mono truncate">
                              Portas: {c.ports}
                            </div>
                          )}
                        </div>

                        {/* Métricas de Uso */}
                        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                          {/* CPU e Memória Row */}
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-350 font-mono">
                              <Cpu size={14} className="text-blue-500 shrink-0" />
                              <span>{c.cpu_perc}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-350 justify-end font-mono">
                              <span>{c.mem_perc}</span>
                            </div>
                          </div>

                          {/* Barra de Progresso Memória */}
                          <div className="w-full bg-slate-200 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                memPercVal > 85 ? 'bg-red-500' : memPercVal > 60 ? 'bg-amber-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${Math.min(memPercVal, 100)}%` }}
                            />
                          </div>
                          <div className="text-right text-xxs text-slate-450 font-mono">
                            {c.mem_usage_limit}
                          </div>

                          {/* Tráfego I/O e Rede */}
                          <div className="grid grid-cols-2 gap-2 pt-1.5 text-xxs border-t border-slate-100/50 dark:border-slate-800/30">
                            <div className="flex items-center gap-1 text-slate-500 dark:text-slate-450" title="Disk I/O">
                              <HardDrive size={12} className="text-slate-450 shrink-0" />
                              <span className="truncate font-mono">Disk: {c.block_io}</span>
                            </div>
                            <div className="flex items-center gap-1 text-slate-500 dark:text-slate-450 justify-end" title="Network I/O">
                              <Network size={12} className="text-slate-450 shrink-0" />
                              <span className="truncate font-mono text-right">Net: {c.net_io}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default DashboardPage